import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Receipt, ExternalLink, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";

const INV_STATUS = {
  paid: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  open: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  void: "bg-slate-500/15 text-slate-300 border-slate-500/30",
  uncollectible: "bg-red-500/15 text-red-400 border-red-500/30",
  draft: "bg-slate-500/15 text-slate-300 border-slate-500/30",
};

export default function AdvertiserInvoicesDialog({ advertiserId, name }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!open) return;
    setData(null);
    api.get(`/admin/advertisers/${advertiserId}/invoices`).then((r) => setData(r.data)).catch((e) => { toast.error(apiError(e.response?.data?.detail)); setData({ invoices: [] }); });
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="ghost" title="Stripe invoices" data-testid={`advertiser-invoices-${advertiserId}`}><Receipt className="w-4 h-4" /></Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl" data-testid="advertiser-invoices-dialog">
        <DialogHeader><DialogTitle>Stripe invoices — {name}</DialogTitle></DialogHeader>
        {data === null ? (
          <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : (
          <>
            {data.coverage && (
              <div className="text-sm text-muted-foreground">Coverage: {data.coverage.coverageStart ? `${data.coverage.coverageStart} → ${data.coverage.coverageEnd}` : "—"} · Status: {data.coverage.status}</div>
            )}
            {(!data.invoices || data.invoices.length === 0) ? (
              <div className="py-8 text-center text-sm text-muted-foreground" data-testid="advertiser-invoices-empty">No Stripe invoices for this advertiser yet.</div>
            ) : (
              <div className="rounded-xl border border-border divide-y divide-border max-h-[50vh] overflow-y-auto">
                {data.invoices.map((r) => (
                  <div key={r.id} className="flex items-center gap-3 p-3" data-testid={`advertiser-invoice-${r.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm flex items-center gap-2">{r.number || r.id}<Badge className={`${INV_STATUS[r.status] || INV_STATUS.draft} border text-[10px]`}>{r.status}</Badge></div>
                      <div className="text-xs text-muted-foreground truncate">${Number(r.amount).toFixed(2)} {r.currency} · {r.date ? new Date(r.date).toLocaleDateString() : ""}{r.periodStart ? ` · ${r.periodStart}→${r.periodEnd}` : ""}</div>
                    </div>
                    {r.hostedUrl && <Button size="sm" variant="ghost" onClick={() => window.open(r.hostedUrl, "_blank")}><ExternalLink className="w-4 h-4 mr-1" />View</Button>}
                    {r.pdfUrl && <Button size="sm" variant="outline" onClick={() => window.open(r.pdfUrl, "_blank")}><Download className="w-4 h-4 mr-1" />PDF</Button>}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
